import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ui, use_slice_user } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_errors from '@/locales/keys/i18n_errors.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { REMOTE__user_auth_login_with_email_and_password } from '@/state/user/auth/login/REMOTE__user_auth_login_with_email_and_password';
import { LOCAL__workspace_set_is_all_data_loaded } from '@/state/workspace/entity/LOCAL__workspace_set_is_all_data_loaded';
import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { DTO_RESPONSE__auth_success } from '@shared/dto/+user/auth/_requests/response_auth_success';
import { DTO__user_credentials } from '@shared/dto/+user/auth/DTO__user_credentials';
import { DTO_RESPONSE__error } from '@shared/network/DTO_RESPONSE__error';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';

export function use_component_action_button_auth_with_email_and_password(email: string, password: string) {
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_AUTH);

  const dispatch = use_dispatch();
  const navigate = useNavigate();

  const { toast } = useToast();

  const { page_auth_is_loading } = use_slice_ui(['page_auth_is_loading']);

  const { open_page_after_auth } = use_slice_user(['open_page_after_auth']);

  const schema_email = z.string().email(t(i18n_errors.auth.email_invalid, { ns: ENUM__i18n_namespace.ERRORS }));
  const schema_password = z.string().min(6, t(i18n_errors.auth.password_length, { ns: ENUM__i18n_namespace.ERRORS }));

  const can_try_auth_with_email_and_password = useMemo(() => !page_auth_is_loading, [email, password, page_auth_is_loading]);

  /*
   * METHODS
   */

  async function auth_with_email_and_password() {
    if (page_auth_is_loading) return;

    const validation_email = schema_email.safeParse(email);
    const validation_password = schema_password.safeParse(password);

    let errors: string[] = [];

    if (validation_email.error) {
      errors.push([...validation_email.error.errors.map(err => err.message)].join('\n'));
    }

    if (validation_password.error) {
      errors.push([...validation_password.error.errors.map(err => err.message)].join('\n'));
    }

    if (validation_password.success && validation_email.success) {
      dispatch(LOCAL__ui_set_boolean({ key: 'page_auth_is_loading', value: true }));

      dispatch(LOCAL__workspace_set_is_all_data_loaded(false));
      dispatch(LOCAL__ui_set_boolean({ value: false, key: 'global_need_show_first_animation' }));

      const credentials = new DTO__user_credentials(email, password);

      let action_result = await dispatch(REMOTE__user_auth_login_with_email_and_password({ credentials }));

      if (action_result.payload instanceof DTO_RESPONSE__auth_success) {
        await navigate(open_page_after_auth);
      } else if (action_result.payload instanceof DTO_RESPONSE__error && action_result.payload.type === ENUM__error_type.AUTH_EMAIL_IS_NOT_VERIFIED) {
        dispatch(LOCAL__workspace_set_is_all_data_loaded(true));

        dispatch(LOCAL__ui_set_boolean({ key: 'page_auth_show_email_is_not_verified', value: true }));
      } else {
        //show page auth again
        dispatch(LOCAL__workspace_set_is_all_data_loaded(true));
      }

      dispatch(LOCAL__ui_set_boolean({ key: 'page_auth_is_loading', value: false }));
    }

    if (errors.length > 0) toast({ title: t(i18n_errors.toast_title_response_error, { ns: ENUM__i18n_namespace.ERRORS }), description: errors.join('\n') });
  }

  return { can_try_auth_with_email_and_password, auth_with_email_and_password };
}
